import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPlus, FaTimes, FaGripVertical, FaSearch, FaChevronDown, FaChevronUp, FaSave, FaArrowLeft, FaDumbbell, FaTrash, FaYoutube, FaEdit, FaStickyNote } from 'react-icons/fa';
import { createUserWorkout, updateUserWorkout, getUserWorkouts } from '../../services/fitnessService';
import { createCoachWorkout, updateCoachWorkout } from '../../services/coachFitnessService';
import toast from 'react-hot-toast';
import './WorkoutCreator.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const CATEGORIES = ['strength', 'cardio', 'hiit', 'flexibility', 'mobility', 'full-body', 'upper-body', 'lower-body', 'core'];
const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];

const getYouTubeThumbUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
};

const getExerciseMedia = (ex) => {
  const data = ex._exerciseData || (typeof ex.exercise === 'object' ? ex.exercise : null);
  return { image: data?.image, video: data?.video, name: data?.name || ex.exercise?.name || 'Exercise' };
};

const getOwnedExerciseDoc = (ex) => {
  const doc = ex._exerciseData || (typeof ex.exercise === 'object' && ex.exercise !== null ? ex.exercise : null);
  return doc && doc._id ? doc : null;
};

const emptyExercise = () => ({
  _key: Date.now() + Math.random(),
  exercise: null,
  exerciseId: '',
  order: 0,
  sets: 3,
  reps: 10,
  duration: null,
  restAfter: 45,
  notes: '',
  isSuperset: false,
  supersetGroup: null
});

const emptyBlock = (name = '') => ({
  _key: Date.now() + Math.random(),
  name: name || 'Block',
  kind: 'main',
  structureType: 'straight_sets',
  structureConfig: {},
  description: '',
  rounds: 1,
  restBetweenRounds: 60,
  exercises: []
});

export default function WorkoutCreator({ isCoach = false }) {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!editId);

  // Workout fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [goal, setGoal] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState(30);
  const [difficulty, setDifficulty] = useState('beginner');
  const [category, setCategory] = useState('strength');
  const [targetAreas, setTargetAreas] = useState('');
  const [equipmentNeeded, setEquipmentNeeded] = useState('');
  const [tags, setTags] = useState('');

  // Block structure
  const [warmup, setWarmup] = useState(null);
  const [blocks, setBlocks] = useState([emptyBlock('Main Block')]);
  const [cooldown, setCooldown] = useState(null);

  // Exercise search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activePickerTarget, setActivePickerTarget] = useState(null); // { section, blockIndex, exerciseIndex }
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);

  // Collapsed state for blocks
  const [collapsedBlocks, setCollapsedBlocks] = useState({});

  // Post-save navigation
  const [showPostSave, setShowPostSave] = useState(false);

  // Load existing workout for editing
  useEffect(() => {
    if (!editId) return;
    const loadWorkout = async () => {
      try {
        // Try user workouts first, then coach workouts
        if (isCoach) {
          const { getCoachWorkouts } = await import('../../services/coachFitnessService');
          const data = await getCoachWorkouts();
          const workouts = data.workouts || data;
          const workout = workouts.find(w => w._id === editId);
          if (workout) populateForm(workout);
        } else {
          const workouts = await getUserWorkouts();
          const workout = workouts.find(w => w._id === editId);
          if (workout) populateForm(workout);
        }
      } catch (err) {
        toast.error('Failed to load workout');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadWorkout();
  }, [editId, isCoach]);

  const populateForm = (workout) => {
    setName(workout.name || '');
    setDescription(workout.description || '');
    setGoal(workout.goal || '');
    setEstimatedDuration(workout.estimatedDuration || 30);
    setDifficulty(workout.difficulty || 'beginner');
    setCategory(workout.category || 'strength');
    setTargetAreas((workout.targetAreas || []).join(', '));
    setEquipmentNeeded((workout.equipmentNeeded || []).join(', '));
    setTags((workout.tags || []).join(', '));
    setWarmup(workout.warmup ? { ...workout.warmup, _key: Date.now(), exercises: (workout.warmup.exercises || []).map(e => ({ ...e, _key: Date.now() + Math.random() })) } : null);
    setBlocks(
      (workout.blocks || []).length > 0
        ? workout.blocks.map(b => ({ ...b, _key: Date.now() + Math.random(), exercises: (b.exercises || []).map(e => ({ ...e, _key: Date.now() + Math.random() })) }))
        : [emptyBlock('Main Block')]
    );
    setCooldown(workout.cooldown ? { ...workout.cooldown, _key: Date.now(), exercises: (workout.cooldown.exercises || []).map(e => ({ ...e, _key: Date.now() + Math.random() })) } : null);
  };

  // Exercise search with debounce
  const searchExercises = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await fetch(`${API_URL}/fitness/exercises-paginated?search=${encodeURIComponent(query)}&limit=20`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.exercises || data);
      }
    } catch (err) {
      console.error('Exercise search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchExercises(searchQuery), 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery, searchExercises]);

  // Close picker on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setActivePickerTarget(null);
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Block helpers
  const toggleCollapse = (key) => {
    setCollapsedBlocks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const addBlock = () => {
    setBlocks(prev => [...prev, emptyBlock(`Block ${prev.length + 1}`)]);
  };

  const removeBlock = (index) => {
    setBlocks(prev => prev.filter((_, i) => i !== index));
  };

  const updateBlock = (index, field, value) => {
    setBlocks(prev => prev.map((b, i) => i === index ? { ...b, [field]: value } : b));
  };

  const toggleWarmup = () => {
    setWarmup(prev => prev ? null : { ...emptyBlock('Warm-up'), name: 'Warm-up', kind: 'warmup' });
  };

  const toggleCooldown = () => {
    setCooldown(prev => prev ? null : { ...emptyBlock('Cool-down'), name: 'Cool-down', kind: 'cooldown' });
  };

  // Exercise helpers for a given section
  const getExercises = (section, blockIndex) => {
    if (section === 'warmup') return warmup?.exercises || [];
    if (section === 'cooldown') return cooldown?.exercises || [];
    return blocks[blockIndex]?.exercises || [];
  };

  const setExercises = (section, blockIndex, exercises) => {
    if (section === 'warmup') {
      setWarmup(prev => ({ ...prev, exercises }));
    } else if (section === 'cooldown') {
      setCooldown(prev => ({ ...prev, exercises }));
    } else {
      setBlocks(prev => prev.map((b, i) => i === blockIndex ? { ...b, exercises } : b));
    }
  };

  const addExerciseToSection = (section, blockIndex, exercise) => {
    const current = getExercises(section, blockIndex);
    const newEx = {
      ...emptyExercise(),
      exercise: exercise._id,
      exerciseId: exercise.exerciseId,
      _exerciseData: exercise, // Keep reference for display
      order: current.length
    };
    setExercises(section, blockIndex, [...current, newEx]);
    setActivePickerTarget(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  const removeExercise = (section, blockIndex, exerciseIndex) => {
    const current = getExercises(section, blockIndex);
    setExercises(section, blockIndex, current.filter((_, i) => i !== exerciseIndex).map((e, i) => ({ ...e, order: i })));
  };

  const updateExercise = (section, blockIndex, exerciseIndex, field, value) => {
    const current = getExercises(section, blockIndex);
    setExercises(section, blockIndex, current.map((e, i) => i === exerciseIndex ? { ...e, [field]: value } : e));
  };

  const moveExercise = (section, blockIndex, fromIndex, direction) => {
    const current = [...getExercises(section, blockIndex)];
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= current.length) return;
    [current[fromIndex], current[toIndex]] = [current[toIndex], current[fromIndex]];
    setExercises(section, blockIndex, current.map((e, i) => ({ ...e, order: i })));
  };

  // Build the workout data payload
  const buildPayload = () => {
    const toOptionalInt = (value) => {
      if (value === '' || value == null) return undefined;
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    };

    const cleanStructureConfig = (config) => {
      if (!config || typeof config !== 'object') return undefined;
      const out = {};
      const maybeInt = (key, { allowZero = false } = {}) => {
        const v = toOptionalInt(config[key]);
        if (v == null) return;
        if (allowZero ? v >= 0 : v > 0) out[key] = v;
      };

      maybeInt('workSec');
      maybeInt('restSec', { allowZero: true });
      maybeInt('durationSec');
      maybeInt('timeCapSec');
      maybeInt('targetRounds');
      maybeInt('emomIntervalSec');
      maybeInt('tabataWorkSec');
      maybeInt('tabataRestSec', { allowZero: true });
      maybeInt('tabataCycles');

      if (typeof config.notes === 'string' && config.notes.trim()) {
        out.notes = config.notes.trim().slice(0, 300);
      }

      return Object.keys(out).length ? out : undefined;
    };

    const cleanExercises = (exercises) =>
      (exercises || []).map((e, i) => ({
        exercise: e.exercise?._id || e.exercise,
        exerciseId: e.exerciseId || e._exerciseData?.exerciseId || '',
        order: i,
        sets: toOptionalInt(e.sets) ?? 3,
        reps: toOptionalInt(e.reps),
        duration: toOptionalInt(e.duration),
        restAfter: toOptionalInt(e.restAfter) ?? 45,
        notes: e.notes || '',
        isSuperset: e.isSuperset || false,
        supersetGroup: e.supersetGroup || undefined
      }));

    const cleanBlock = (block) => block ? {
      name: block.name,
      kind: block.kind || undefined,
      structureType: block.structureType || undefined,
      structureConfig: cleanStructureConfig(block.structureConfig),
      description: block.description || '',
      rounds: toOptionalInt(block.rounds) ?? 1,
      restBetweenRounds: toOptionalInt(block.restBetweenRounds) ?? 60,
      exercises: cleanExercises(block.exercises)
    } : null;

    return {
      name,
      description,
      goal,
      estimatedDuration: parseInt(estimatedDuration, 10) || 30,
      difficulty,
      category,
      targetAreas: targetAreas.split(',').map(s => s.trim()).filter(Boolean),
      warmup: warmup ? cleanBlock(warmup) : undefined,
      blocks: blocks.map(cleanBlock).filter(Boolean),
      cooldown: cooldown ? cleanBlock(cooldown) : undefined,
      equipmentNeeded: equipmentNeeded.split(',').map(s => s.trim()).filter(Boolean),
      tags: tags.split(',').map(s => s.trim()).filter(Boolean)
    };
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Workout name is required');
      return;
    }
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      if (editId) {
        if (isCoach) {
          await updateCoachWorkout(editId, payload);
        } else {
          await updateUserWorkout(editId, payload);
        }
        toast.success('Workout updated!');
      } else {
        if (isCoach) {
          await createCoachWorkout(payload);
        } else {
          await createUserWorkout(payload);
        }
        toast.success('Workout created!');
      }
      setShowPostSave(true);
    } catch (err) {
      toast.error(err.message || 'Failed to save workout');
    } finally {
      setSaving(false);
    }
  };

  // Exercise picker component
  const ExercisePicker = ({ section, blockIndex }) => {
    const isActive = activePickerTarget?.section === section && activePickerTarget?.blockIndex === blockIndex;
    return (
      <div className="exercise-picker-wrapper">
        <button
          type="button"
          className="add-exercise-btn"
          onClick={() => {
            setActivePickerTarget(isActive ? null : { section, blockIndex });
            setSearchQuery('');
            setSearchResults([]);
          }}
        >
          <FaPlus /> Add Exercise
        </button>
        {isActive && (
          <div className="exercise-picker-dropdown" ref={searchRef}>
            <div className="picker-search">
              <FaSearch />
              <input
                type="text"
                placeholder="Search exercises..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            <div className="picker-results">
              {searchLoading && <div className="picker-loading">Searching...</div>}
              {!searchLoading && searchResults.length === 0 && searchQuery.length >= 2 && (
                <div className="picker-empty">No exercises found</div>
              )}
              {searchResults.map(ex => (
                <button
                  key={ex._id}
                  type="button"
                  className="picker-result-item"
                  onClick={() => addExerciseToSection(section, blockIndex, ex)}
                >
                  <div className="picker-result-thumb">
                    {ex.image ? (
                      <img src={ex.image} alt="" />
                    ) : getYouTubeThumbUrl(ex.video) ? (
                      <img src={getYouTubeThumbUrl(ex.video)} alt="" />
                    ) : (
                      <FaDumbbell />
                    )}
                  </div>
                  <div className="picker-result-info">
                    <div className="picker-result-name">{ex.name}</div>
                    <div className="picker-result-meta">
                      {ex.category && <span>{ex.category}</span>}
                      {ex.difficulty && <span>{ex.difficulty}</span>}
                      {ex.targetMuscles?.length > 0 && <span>{ex.targetMuscles.slice(0, 2).join(', ')}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Exercise row component
  const ExerciseRow = ({ ex, index, section, blockIndex, total }) => {
    const media = getExerciseMedia(ex);
    const thumbUrl = media.image || getYouTubeThumbUrl(media.video);
    const ownedDoc = getOwnedExerciseDoc(ex);
    const canEditOwnedExercise =
      Boolean(user?.sub && ownedDoc?.userId && ownedDoc.userId === user.sub && ownedDoc._id);
    return (
      <div className="exercise-row">
        <div className="exercise-row-header">
          <div className="exercise-row-left">
            <div className="exercise-reorder">
              <button type="button" title="Move up" disabled={index === 0} onClick={() => moveExercise(section, blockIndex, index, -1)}><FaChevronUp /></button>
              <button type="button" title="Move down" disabled={index === total - 1} onClick={() => moveExercise(section, blockIndex, index, 1)}><FaChevronDown /></button>
            </div>
            <div className="exercise-row-thumb">
              {thumbUrl ? (
                <img src={thumbUrl} alt="" />
              ) : media.video ? (
                <FaYoutube className="thumb-icon youtube" />
              ) : (
                <FaDumbbell className="thumb-icon" />
              )}
            </div>
            <span className="exercise-name" title={media.name}>{media.name}</span>
          </div>
          <div className="exercise-row-header-actions">
            {canEditOwnedExercise && (
              <button
                type="button"
                className="edit-exercise-inline-btn"
                title="Edit exercise"
                onClick={() =>
                  navigate(isCoach ? `/coach/exercise/${ownedDoc._id}/edit` : `/fitness/exercise/${ownedDoc._id}/edit`)
                }
              >
                <FaEdit />
              </button>
            )}
            <button type="button" className="remove-exercise-btn" title="Remove exercise" onClick={() => removeExercise(section, blockIndex, index)}>
              <FaTimes />
            </button>
          </div>
        </div>
        <div className="exercise-config">
          <label>
            Sets
            <input type="number" min="1" max="20" value={ex.sets} onChange={e => updateExercise(section, blockIndex, index, 'sets', e.target.value)} />
          </label>
          <label>
            Reps
            <input type="number" min="1" max="100" value={ex.reps || ''} placeholder="—" onChange={e => updateExercise(section, blockIndex, index, 'reps', e.target.value || null)} />
          </label>
          <label>
            Duration (s)
            <input type="number" min="0" max="600" value={ex.duration || ''} placeholder="—" onChange={e => updateExercise(section, blockIndex, index, 'duration', e.target.value || null)} />
          </label>
          <label>
            Rest (s)
            <input type="number" min="0" max="300" value={ex.restAfter} onChange={e => updateExercise(section, blockIndex, index, 'restAfter', e.target.value)} />
          </label>
        </div>
        <div className="exercise-notes-row">
          <label className="exercise-notes-label"><FaStickyNote /> Notes</label>
          <input type="text" placeholder="e.g. Keep core tight, go slow on eccentric..." value={ex.notes || ''} onChange={e => updateExercise(section, blockIndex, index, 'notes', e.target.value)} />
        </div>
      </div>
    );
  };

  // Render a block section (warmup, block, or cooldown)
  const BlockSection = ({ block, section, blockIndex, onRemove }) => {
    const isBlock = section === 'block';
    const collapsed = collapsedBlocks[block._key];
    const exercises = block.exercises || [];

    return (
      <div className="block-section">
        <div className="block-header" onClick={() => toggleCollapse(block._key)}>
          <div className="block-header-left">
            <FaGripVertical className="block-grip" />
            {isBlock ? (
              <input
                type="text"
                className="block-name-input"
                value={block.name}
                onChange={e => updateBlock(blockIndex, 'name', e.target.value)}
                onClick={e => e.stopPropagation()}
                placeholder="Block name"
              />
            ) : (
              <span className="block-name">{block.name}</span>
            )}
            <span className="block-exercise-count">{exercises.length} exercise{exercises.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="block-header-right">
            {isBlock && (
              <button type="button" className="block-remove-btn" onClick={e => { e.stopPropagation(); onRemove(); }}>
                <FaTrash />
              </button>
            )}
            {!isBlock && (
              <button type="button" className="block-remove-btn" onClick={e => { e.stopPropagation(); section === 'warmup' ? toggleWarmup() : toggleCooldown(); }}>
                <FaTimes />
              </button>
            )}
            {collapsed ? <FaChevronDown /> : <FaChevronUp />}
          </div>
        </div>

        {!collapsed && (
          <div className="block-body">
            {isBlock && (
              <div className="block-meta">
                <label>
                  Rounds
                  <input type="number" min="1" max="10" value={block.rounds} onChange={e => updateBlock(blockIndex, 'rounds', e.target.value)} />
                </label>
                <label>
                  Rest between rounds (s)
                  <input type="number" min="0" max="300" value={block.restBetweenRounds} onChange={e => updateBlock(blockIndex, 'restBetweenRounds', e.target.value)} />
                </label>
                <label className="block-desc-label">
                  Description
                  <input type="text" value={block.description || ''} onChange={e => updateBlock(blockIndex, 'description', e.target.value)} placeholder="Optional" />
                </label>
              </div>
            )}

            <div className="block-exercises">
              {exercises.map((ex, i) => (
                <ExerciseRow
                  key={ex._key || i}
                  ex={ex}
                  index={i}
                  section={section}
                  blockIndex={blockIndex}
                  total={exercises.length}
                />
              ))}
            </div>

            <ExercisePicker section={section} blockIndex={blockIndex} />
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="workout-creator">
        <div className="creator-loading">
          <div className="loading-spinner" />
          <p>Loading workout...</p>
        </div>
      </div>
    );
  }

  const canSave = name.trim() && description.trim() && !saving;

  return (
    <div className="workout-creator">
      <div className="creator-container">
        <div className="creator-top-bar">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back
          </button>
          <h1>{editId ? 'Edit Workout' : 'Create Workout'}</h1>
          <button type="button" className="save-btn" onClick={handleSave} disabled={!canSave}>
            <FaSave /> {saving ? 'Saving...' : 'Save Workout'}
          </button>
        </div>

        {/* Basic Info */}
        <section className="creator-section">
          <h2>Basic Info</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Workout Name *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Full Body Strength" />
            </div>
            <div className="form-group full-width">
              <label>Description *</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe this workout..." rows={3} />
            </div>
            <div className="form-group">
              <label>Goal</label>
              <input type="text" value={goal} onChange={e => setGoal(e.target.value)} placeholder="e.g. Build overall strength" />
            </div>
            <div className="form-group">
              <label>Duration (min)</label>
              <input type="number" min="5" max="180" value={estimatedDuration} onChange={e => setEstimatedDuration(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                {DIFFICULTIES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Target Areas</label>
              <input type="text" value={targetAreas} onChange={e => setTargetAreas(e.target.value)} placeholder="glutes, core, hamstrings" />
            </div>
            <div className="form-group">
              <label>Equipment Needed</label>
              <input type="text" value={equipmentNeeded} onChange={e => setEquipmentNeeded(e.target.value)} placeholder="dumbbells, bench" />
            </div>
            <div className="form-group full-width">
              <label>Tags</label>
              <input type="text" value={tags} onChange={e => setTags(e.target.value)} placeholder="beginner-friendly, no-jump" />
            </div>
          </div>
        </section>

        {/* Workout Structure */}
        <section className="creator-section">
          <h2>Workout Structure</h2>

          <div className="structure-toggles">
            {!warmup && <button type="button" className="toggle-section-btn" onClick={toggleWarmup}><FaPlus /> Add Warm-up</button>}
            {!cooldown && <button type="button" className="toggle-section-btn" onClick={toggleCooldown}><FaPlus /> Add Cool-down</button>}
          </div>

          {warmup && <BlockSection block={warmup} section="warmup" blockIndex={-1} />}

          {blocks.map((block, i) => (
            <BlockSection
              key={block._key || i}
              block={block}
              section="block"
              blockIndex={i}
              onRemove={() => removeBlock(i)}
            />
          ))}

          <button type="button" className="add-block-btn" onClick={addBlock}>
            <FaPlus /> Add Block
          </button>

          {cooldown && <BlockSection block={cooldown} section="cooldown" blockIndex={-1} />}
        </section>
      </div>

      {/* Post-save navigation modal */}
      {showPostSave && (
        <div className="post-save-overlay">
          <div className="post-save-modal">
            <h2>Workout Saved!</h2>
            <p>What would you like to do next?</p>
            <div className="post-save-actions">
              {isCoach ? (
                <>
                  <button className="post-save-btn coach" onClick={() => navigate('/coach/dashboard')}>
                    Go to Coach Dashboard
                  </button>
                </>
              ) : (
                <button className="post-save-btn workouts" onClick={() => navigate('/fitness/workouts')}>
                  Go to Workouts
                </button>
              )}
              <button className="post-save-btn stay" onClick={() => setShowPostSave(false)}>
                Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
